import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

import {
  handleOpenMachineController,
  selectEssSwitch,
} from 'shared/slice/essSwitchSlice';
import {
  tgsHandleOpenMachineController,
  selectTgsSwitch,
} from 'shared/slice/tgsSwitchSlice';
import {
  tesHandleOpenMachineController,
  selectTesSwitch,
} from 'shared/slice/tesSwitchSlice';

import { layerA } from 'shared/styles/commonStyles';

import LocationPanel from './LocationPanel';
import NavPanel from './NavPanel';

const OPEN_MC_ACTIONS = {
  ess: handleOpenMachineController,
  tgs: tgsHandleOpenMachineController,
  tes: tesHandleOpenMachineController,
};

const SwitchLayout = ({ swtName, isMasterControl }) => {
  const isMobile = useMediaQuery({ query: '(max-width:600px)' });
  const dispatch = useDispatch();

  const essData = useSelector(selectEssSwitch);
  const tgsData = useSelector(selectTgsSwitch);
  const tesData = useSelector(selectTesSwitch);

  const switchData =
    swtName === 'ess'
      ? essData.essSwitch
      : swtName === 'tgs'
      ? tgsData.tgsSwitch
      : tesData.tesSwitch;

  const flatSwitch =
    swtName === 'ess'
      ? essData.flatEssSwitch
      : swtName === 'tgs'
      ? tgsData.flatTgsSwitch
      : tesData.flatTesSwitch;

  const openMachineController = OPEN_MC_ACTIONS[swtName];

  // the selection lives in the url so a zone or switch can be linked, survives a
  // refresh, and the back button steps back through it.
  // note: `switch` is taken by the one-shot deep link SwitchMain consumes and
  // then deletes, so the persistent machine selection uses `machine`
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedLocation = searchParams.get('location');
  const selectedSpecificLocation = searchParams.get('zone');
  const selectedMachine = searchParams.get('machine');

  const updateSelection = ({ location, zone, machine }) => {
    const next = new URLSearchParams(searchParams);

    [
      ['location', location],
      ['zone', zone],
      ['machine', machine],
    ].forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)));

    setSearchParams(next);
  };

  const effectiveLocationId = selectedSpecificLocation || selectedLocation;

  // a link opened straight onto a switch never went through the click handler,
  // so the controller still has to be told which machine is showing.
  // the reducer indexes flatSwitch[location][machine] blind, so this has to wait
  // for the fetch to land and ignore a url naming a switch that is not there
  useEffect(() => {
    if (!effectiveLocationId || !selectedMachine) return;
    if (!flatSwitch?.[effectiveLocationId]?.[selectedMachine]) return;

    dispatch(
      openMachineController({
        location: effectiveLocationId,
        machine: selectedMachine,
        status: true,
      })
    );
  }, [
    dispatch,
    openMachineController,
    flatSwitch,
    effectiveLocationId,
    selectedMachine,
  ]);

  const mobileView = selectedMachine ? 'detail' : selectedLocation ? 'zone' : 'nav';
  const detailViewKey = selectedMachine
    ? `switch-${effectiveLocationId}-${selectedMachine}`
    : effectiveLocationId
    ? `zone-${effectiveLocationId}`
    : 'empty';

  const getMachines = () => {
    if (!selectedLocation) return [];

    const isSpecificLocation = switchData[selectedLocation]?.isSpecificLocation;

    if (isSpecificLocation && selectedSpecificLocation) {
      return Object.keys(
        switchData[selectedLocation]?.subLocations?.[selectedSpecificLocation]?.devices || {}
      );
    }

    if (!isSpecificLocation) {
      return Object.keys(switchData[selectedLocation]?.devices || {});
    }

    return [];
  };

  // the selection comes off the url now, so it can name a location this system
  // has no entry for — closing a controller there would throw in the reducer
  const closeControllers = (locationId, machineIds) => {
    machineIds.forEach((machineId) => {
      if (!flatSwitch?.[locationId]?.[machineId]) return;

      dispatch(
        openMachineController({ location: locationId, machine: machineId, status: false })
      );
    });
  };

  const handleSelectLocation = (locationId, specificLocationId) => {
    const previousLocationId = selectedSpecificLocation || selectedLocation;

    if (previousLocationId && selectedMachine) {
      closeControllers(previousLocationId, getMachines());
    }

    updateSelection({ location: locationId, zone: specificLocationId || null, machine: null });
  };

  const handleSelectSpecificLocation = (specificLocationId) => {
    if (!selectedLocation || !specificLocationId) return;
    handleSelectLocation(selectedLocation, specificLocationId);
  };

  const handleClearSpecificLocation = () => {
    if (selectedMachine && effectiveLocationId) {
      closeControllers(effectiveLocationId, [selectedMachine]);
    }

    updateSelection({ location: selectedLocation, zone: null, machine: null });
  };

  const handleSelectMachine = (machineId) => {
    getMachines().forEach((currentMachineId) => {
      if (!flatSwitch?.[effectiveLocationId]?.[currentMachineId]) return;

      dispatch(
        openMachineController({
          location: effectiveLocationId,
          machine: currentMachineId,
          status: currentMachineId === machineId,
        })
      );
    });

    updateSelection({
      location: selectedLocation,
      zone: selectedSpecificLocation,
      machine: machineId,
    });
  };

  const handleClearMachine = () => {
    updateSelection({
      location: selectedLocation,
      zone: selectedSpecificLocation,
      machine: null,
    });
  };

  const handleClearLocation = () => {
    updateSelection({ location: null, zone: null, machine: null });
  };

  const navPanel = (
    <NavPanel
      swtName={swtName}
      switchData={switchData}
      flatSwitch={flatSwitch}
      selectedLocation={selectedLocation}
      selectedSpecificLocation={selectedSpecificLocation}
      onSelectLocation={handleSelectLocation}
    />
  );

  const detailPanel = (
    <LocationPanel
      swtName={swtName}
      switchData={switchData}
      selectedLocation={selectedLocation}
      selectedSpecificLocation={selectedSpecificLocation}
      selectedMachine={selectedMachine}
      flatSwitch={flatSwitch}
      onSelectMachine={handleSelectMachine}
      onClearMachine={handleClearMachine}
      onSelectSpecificLocation={handleSelectSpecificLocation}
      onClearSpecificLocation={handleClearSpecificLocation}
    />
  );

  if (isMobile) {
    return (
      <MobileWrapper>
        {mobileView === 'detail' && selectedMachine ? (
          <>
            <MobileBackButton onClick={handleClearMachine}>
              Back to Zone
            </MobileBackButton>
            {detailPanel}
          </>
        ) : mobileView === 'zone' && selectedLocation ? (
          <>
            <MobileBackButton onClick={handleClearLocation}>
              Back to Locations
            </MobileBackButton>
            {detailPanel}
          </>
        ) : (
          navPanel
        )}
      </MobileWrapper>
    );
  }

  return (
    <LayoutWrapper>
      {isMasterControl && <ProgramsRow />}
      <ContentRow>
        <NavColumn>{navPanel}</NavColumn>
        <DetailStage $hasSelection={Boolean(selectedLocation)}>
          <DetailPanelViewport
            key={detailViewKey}
            $mode={selectedMachine ? 'switch' : selectedLocation ? 'zone' : 'empty'}
          >
            {detailPanel}
          </DetailPanelViewport>
        </DetailStage>
      </ContentRow>
    </LayoutWrapper>
  );
};

const PROGRAM_CARDS = [
  { key: 'instantHeat', label: 'Instant Heat Program', icon: '/images/logo-instantHeat.svg', hasTemp: true },
  { key: 'snowSensor', label: 'Snow Sensor Program', icon: '/images/logo-snowSensor.svg', hasTemp: true, hasDefaultTemp: true },
  { key: 'optionalConstantTemp', label: 'Opt.Const.Temp.Program', icon: '/images/logo-constantTemp.svg', hasTemp: true },
  { key: 'heatingSchedule', label: 'Heating Schedule Program', icon: '/images/logo-schedule.svg', hasDateRange: true, hasTemp: true },
  { key: 'windFactor', label: 'Wind Factor Program', icon: '/images/logo-windFactor.svg' },
  { key: 'deactivate', label: 'Deactivate', icon: null },
];

const ProgramsRow = () => (
  <ProgramsWrapper>
    {PROGRAM_CARDS.map(({ key, label, icon, hasTemp, hasDefaultTemp, hasDateRange }) => (
      <ProgramCardMini key={key}>
        <ProgramCardTitle>{label}</ProgramCardTitle>
        {icon && <ProgramIcon src={icon} alt={label} />}
        <ProgramCardBody>
          <SelectPlaceholder>select location ▾</SelectPlaceholder>
          {hasDefaultTemp && <DefaultTempRow>default trigger temperature <strong>350 °C</strong></DefaultTempRow>}
          {hasTemp && <TempInputPlaceholder>input temperature</TempInputPlaceholder>}
          {hasDateRange && (
            <>
              <SelectPlaceholder>start &amp; end date</SelectPlaceholder>
              <SelectPlaceholder>──────────── ▾</SelectPlaceholder>
            </>
          )}
          <ApplyBtnPlaceholder>apply</ApplyBtnPlaceholder>
        </ProgramCardBody>
      </ProgramCardMini>
    ))}
  </ProgramsWrapper>
);

export default SwitchLayout;

const LayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  min-height: 680px;
  padding: 12px;
  ${layerA};
  border: 1px solid rgba(41, 79, 116, 0.58);
  border-radius: 24px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 18px 42px rgba(2, 6, 23, 0.34);

  .light & {
    border-color: var(--switch-border-strong);
    box-shadow: var(--shadow-card);
  }
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 14px;
  flex: 1;
  min-height: 0;
`;

const ProgramsWrapper = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 0 12px 0;
  overflow-x: auto;

  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: rgba(51, 65, 85, 0.6); border-radius: 999px; }
`;

const ProgramCardMini = styled.div`
  min-width: 200px;
  max-width: 220px;
  padding: 16px;
  background: var(--switch-input-bg);
  border: 0.8px solid rgba(29, 41, 61, 0.5);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
`;

const ProgramCardTitle = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--switch-text-primary);
  position: relative;
  z-index: 1;
`;

const ProgramIcon = styled.img`
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 120px;
  object-fit: contain;
  opacity: 0.12;
  pointer-events: none;
`;

const ProgramCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  z-index: 1;
`;

const SelectPlaceholder = styled.div`
  padding: 10px 14px;
  border-radius: 12px;
  border: 0.8px solid rgba(29, 41, 61, 0.5);
  background: var(--switch-panel-bg);
  color: var(--switch-text-faint);
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DefaultTempRow = styled.div`
  font-size: 10px;
  color: var(--switch-text-faint);
  padding: 4px 0;

  strong { color: var(--switch-text-primary); margin-left: 8px; }
`;

const TempInputPlaceholder = styled.div`
  padding: 8px 12px;
  border-radius: 10px;
  border: 0.8px solid rgba(29, 41, 61, 0.5);
  background: var(--switch-panel-bg);
  color: var(--switch-text-faint);
  font-size: 11px;
`;

const ApplyBtnPlaceholder = styled.button`
  padding: 12px;
  border-radius: 14px;
  border: none;
  background: #1a2744;
  color: #90a1b9;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  width: 100%;

  &:hover { background: #243352; }
`;

const NavColumn = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(51, 65, 85, 0.9);
    border-radius: 999px;
  }
`;

const MobileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MobileBackButton = styled.button`
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-bottom: 1px solid #0f1f33;
  color: #4ade80;
  font-size: 12px;
  letter-spacing: 1px;
  cursor: pointer;
  text-align: left;
  flex-shrink: 0;

  &:hover {
    color: #86efac;
  }
`;

const zoneSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(36px) scale(0.985);
  }

  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`;

const switchSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(52px) scale(0.982);
  }

  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`;

const emptyFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const DetailStage = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  position: relative;
  padding: 4px;
  border-radius: 22px;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(51, 65, 85, 0.9);
    border-radius: 999px;
  }
  background:
    radial-gradient(circle at top right, rgba(59, 130, 246, 0.12), transparent 34%),
    linear-gradient(180deg, rgba(11, 17, 32, 0.9) 0%, rgba(7, 12, 22, 0.98) 100%);
  border: 1px solid
    ${({ $hasSelection }) =>
      $hasSelection ? 'rgba(59, 130, 246, 0.16)' : 'rgba(41, 79, 116, 0.42)'};

  .light & {
    background:
      radial-gradient(circle at top right, rgba(59, 130, 246, 0.06), transparent 34%),
      var(--switch-shell-bg);
    border-color: ${({ $hasSelection }) =>
      $hasSelection ? 'var(--switch-blue-border)' : 'var(--switch-border-strong)'};
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(135deg, rgba(148, 163, 184, 0.04), transparent 36%),
      radial-gradient(circle at 10% 18%, rgba(34, 197, 94, 0.09), transparent 22%);
    pointer-events: none;
  }
`;

const DetailPanelViewport = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  min-width: 0;
  display: flex;
  animation: ${({ $mode }) =>
      $mode === 'switch' ? switchSlideIn : $mode === 'zone' ? zoneSlideIn : emptyFadeIn}
    260ms cubic-bezier(0.22, 1, 0.36, 1);
`;
